// The one HTTP client: adds the auth header, a timeout, and turns failures
// into ApiRequestError so callers can tell "offline" from "refused".
import axios, { type AxiosError } from 'axios';
import { env } from '@/config/env';
import { i18n } from '@/i18n';
import type { ApiError } from '@/types/api';
import { session } from './session';

export class ApiRequestError extends Error {
  readonly status: number | null;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number | null, error: ApiError) {
    super(error.message);
    this.status = status;
    this.code = error.code;
    this.details = error.details;
  }

  /** No answer from the server at all (offline, DNS, timeout). */
  get isNetwork(): boolean {
    return this.status === null;
  }
}

let onUnauthorized: () => void = () => {};
export function setUnauthorizedHandler(handler: () => void) {
  onUnauthorized = handler;
}

export const apiClient = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config) => {
  const token = session.token();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ error?: ApiError }>) => {
    // No answer, or a proxy saying the API is unreachable: treat as offline.
    if (!error.response || [502, 503, 504].includes(error.response.status)) {
      return Promise.reject(
        new ApiRequestError(null, { code: 'NETWORK', message: 'No connection to the server' })
      );
    }
    const { status, data } = error.response;
    if (status === 401 && session.token()) onUnauthorized();
    const body = data?.error ?? {
      code: 'INTERNAL',
      message: `Unexpected error (${status})`,
    };
    return Promise.reject(new ApiRequestError(status, body));
  }
);

/** Error codes of docs/API.md (plus NETWORK) that have a translated message. */
const KNOWN_CODES = new Set([
  'NETWORK',
  'VALIDATION',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NOT_FOUND',
  'CONFLICT',
  'RATE_LIMITED',
  'INTERNAL',
]);

/**
 * A message for the user, in the current language. Known API error codes are
 * translated (`errors.<CODE>`); `overrides` gives a more precise text for a
 * code on a given screen (e.g. CONFLICT → "this e-mail is already used").
 * Unknown codes fall back to the server's own message.
 */
export function errorMessage(err: unknown, overrides?: Partial<Record<string, string>>): string {
  if (err instanceof ApiRequestError) {
    const specific = overrides?.[err.code];
    if (specific) return specific;
    if (KNOWN_CODES.has(err.code)) return i18n.t(`errors.${err.code}`);
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return i18n.t('errors.UNKNOWN');
}
